import { createContext, useContext, useEffect, useRef, useState } from 'react'

/** Bir formun kapatma/terk koruması (`useRecordEditor().requestClose`). */
export type CloseGuard = (action: () => void) => void

export interface GuardRegistry {
  /**
   * Sayfanın form dışı bir eylemi (ör. firma seçici) açık formları atacaksa onu bundan geçirir: kirli
   * her form sırayla "Kaydedilmemiş değişiklikler" sorar, hepsi onaylanınca `action` çalışır.
   */
  guarded: (action: () => void) => void
  add: (guard: CloseGuard) => () => void
}

/** Korunan alt ağacın sağlayıcısı: `<GuardScope value={useGuardRegistry()}>`. */
export const GuardScope = createContext<GuardRegistry | null>(null)

/** Sayfa düzeyi koruma kaydı; formlar `GuardScope` altında `useRegisterGuard` ile kaydolur. */
export function useGuardRegistry(): GuardRegistry {
  const [registry] = useState<GuardRegistry>(() => {
    const guards = new Set<CloseGuard>()
    return {
      guarded: (action) =>
        [...guards].reduceRight<() => void>((next, guard) => () => guard(next), action)(),
      add: (guard) => {
        guards.add(guard)
        return () => {
          guards.delete(guard)
        }
      },
    }
  })
  return registry
}

/** Formun güncel korumasını en yakın `GuardScope`'a kaydeder (yoksa bir şey yapmaz). */
export function useRegisterGuard(guard: CloseGuard): void {
  const registry = useContext(GuardScope)
  const latest = useRef(guard)
  useEffect(() => {
    latest.current = guard
  })
  useEffect(() => registry?.add((action) => latest.current(action)), [registry])
}
